/**
 * Verificación de cuenta (Alta.dc.html, pantalla 3).
 *
 * Consume el token del enlace del email al montar. Éxito → iniciar sesión;
 * caducado/inválido → reenviar (si sabemos el email) o volver a empezar.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { resendVerification, verifyEmail, apiMessage } from '../../services/apiClient';
import { AuthLayout, MessageCard, PENDING_EMAIL_KEY } from './AuthLayout';

type Status = 'checking' | 'ok' | 'error';

export function VerificarPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>('checking');
  const ran = useRef(false); // StrictMode monta dos veces y el token es de un solo uso

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const token = params.get('token');
    if (!token) {
      setStatus('error');
      return;
    }
    verifyEmail(token)
      .then(() => setStatus('ok'))
      .catch(() => setStatus('error'));
  }, [params]);

  const pendingEmail = sessionStorage.getItem(PENDING_EMAIL_KEY);
  const [resendError, setResendError] = useState<string | null>(null);
  const resendNew = async () => {
    if (!pendingEmail) return;
    setResendError(null);
    try {
      await resendVerification(pendingEmail);
      // Sólo se navega a "te hemos enviado un enlace" si el reenvío LLEGÓ:
      // el `.catch(() => undefined)` prometía un correo que nunca salió
      // (ronda 3, r2b19).
      navigate('/revisa');
    } catch (err: unknown) {
      setResendError(
        apiMessage(err, 'No se pudo reenviar el enlace. Comprueba tu conexión e inténtalo de nuevo.')
      );
    }
  };

  if (status === 'checking') {
    return (
      <AuthLayout>
        <div className="self-center flex flex-col items-center gap-4 text-ink-2">
          <div className="w-9 h-9 rounded-full border-4 border-brand border-t-transparent animate-spin" />
          Confirmando tu cuenta…
        </div>
      </AuthLayout>
    );
  }

  if (status === 'ok') {
    return (
      <AuthLayout>
        <MessageCard
          icon="check-circle"
          title="Cuenta confirmada"
          body="Ya puedes entrar en Kakuat y configurar tu primer centro de trabajo."
        >
          <Link to="/login" className="btn-primary grid place-items-center no-underline">
            Iniciar sesión
          </Link>
        </MessageCard>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <MessageCard
        icon="alert"
        tone="signal"
        title="Este enlace ya no sirve"
        body="El enlace de confirmación ha caducado o no es válido. Te enviamos uno nuevo en un momento."
      >
        {pendingEmail ? (
          <>
            <button onClick={resendNew} className="btn-primary mb-3">
              Enviar un enlace nuevo
            </button>
            {resendError && (
              <p className="rounded-xl bg-danger-soft text-danger px-4 py-3 text-[14px] mb-3">
                {resendError}
              </p>
            )}
          </>
        ) : (
          <Link to="/registro" className="btn-primary grid place-items-center no-underline mb-3">
            Volver a registrarme
          </Link>
        )}
        <a href="mailto:kakuat@kakuat.com" className="text-[14px] text-ink-2 no-underline">
          ¿Sigue sin funcionar? Escríbenos
        </a>
      </MessageCard>
    </AuthLayout>
  );
}
